import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEmployeeTypes, useCreateEmployeeType, useDeleteEmployeeType } from '../../hooks/useAdmin';
import { Button } from '../../components/common/Button';
import { Spinner } from '../../components/common/Spinner';
import { ConfirmModal } from '../../components/common/Modal';

export default function EmployeeTypePage() {
  const navigate = useNavigate();
  const { data: types, isLoading } = useEmployeeTypes();
  const createMutation = useCreateEmployeeType();
  const deleteMutation = useDeleteEmployeeType();

  const [showForm, setShowForm] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [form, setForm] = useState({ name: '', totalHoursPerMonth: '' });
  const [formError, setFormError] = useState<string | null>(null);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    try {
      await createMutation.mutateAsync({
        name: form.name,
        totalHoursPerMonth: form.totalHoursPerMonth ? parseInt(form.totalHoursPerMonth, 10) : undefined,
      });
      setShowForm(false);
      setForm({ name: '', totalHoursPerMonth: '' });
    } catch (err) {
      setFormError((err as Error).message);
    }
  };

  const handleDelete = async () => {
    if (deleteId === null) return;
    await deleteMutation.mutateAsync(deleteId);
    setDeleteId(null);
  };

  if (isLoading) {
    return <div className="flex justify-center py-8"><Spinner /></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <button onClick={() => navigate('/admin')} className="text-sm text-gray-500 hover:text-gray-700 mb-1">
          &larr; Back to Admin
        </button>
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold text-gray-900">Employee Types</h1>
          <Button size="sm" onClick={() => setShowForm(!showForm)}>
            {showForm ? 'Cancel' : 'Add Type'}
          </Button>
        </div>
      </div>

      {showForm && (
        <form onSubmit={handleCreate} className="bg-white rounded-lg border border-gray-200 p-4 space-y-3">
          {formError && <p className="text-sm text-red-600">{formError}</p>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input
              type="text"
              required
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="Type name *"
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
            <input
              type="number"
              value={form.totalHoursPerMonth}
              onChange={(e) => setForm((prev) => ({ ...prev, totalHoursPerMonth: e.target.value }))}
              placeholder="Hours per month (optional)"
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <Button type="submit" size="sm" isLoading={createMutation.isPending}>Create</Button>
        </form>
      )}

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Hours/Month</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {types?.map((type) => (
              <tr key={type.id} className="hover:bg-gray-50">
                <td className="px-6 py-3 text-sm font-medium text-gray-900">{type.name || '-'}</td>
                <td className="px-6 py-3 text-sm text-gray-600 text-right">{type.totalHoursPerMonth ?? '-'}</td>
                <td className="px-6 py-3 text-right">
                  <button
                    onClick={() => setDeleteId(type.id)}
                    className="text-red-600 hover:text-red-800 text-sm font-medium"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ConfirmModal
        isOpen={deleteId !== null}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Delete Employee Type"
        message="Are you sure you want to delete this employee type?"
        confirmText="Delete"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
